import React from "react";
import { Box, Flex, FlexProps, Text, TextProps } from "@chakra-ui/react";
import CustomIcon from "../../../../../public/icons-v1_1";

// 값 텍스트 기본 스타일 — valueStyle/emptyValueStyle로 덮어쓸 수 있다.
export const DEFAULT_VALUE_STYLE: TextProps = {
  textStyle: "body-sm-bold",
  color: "#444440",
};

export interface MetricRowProps {
  label: string;
  // falsy(0/undefined/null 등)면 emptyText(기본 "-")로 표시한다.
  value?: string | null;
  sub?: string;
  // 값 클릭 시 동작(외부 링크 등). 지정하면 링크 스타일(밑줄)로 렌더된다.
  onClickValue?: () => void;
  // 링크가 아닌 클릭 동작(복사 등)이면 false로 밑줄을 끈다. 대신 hover 색으로 클릭 가능함을 알린다.
  underlineValue?: boolean;
  // 값이 없을 때 "-" 대신 표시할 문구 (기본 "-")
  emptyText?: string;
  // 지정 시 라벨 우측에 인포 아이콘을 렌더한다(값이 있을 때만). 클릭 시 호출.
  onInfoClick?: () => void;
  // 값 텍스트 스타일(색/폰트크기/굵기/textStyle 등) — Chakra Text props로 병합
  valueStyle?: TextProps;
  // 빈값일 때 값 텍스트 스타일 — 미지정 시 기본 스타일(회색)
  emptyValueStyle?: TextProps;
  // 라벨과 값의 배치. "column"이면 값이 **항상** 라벨 아래 줄 우측에 놓인다.
  // Chakra 반응형 값을 그대로 받으므로 호출부가 브레이크포인트를 직접 정할 수 있다.
  // (기본 "row"는 좁을 때만 접히는 아래 wrap 동작을 따른다)
  direction?: FlexProps["flexDirection"];
}

/**
 * 카드의 라벨/값 한 줄.
 *
 * 기본(`row`)은 값이 라벨과 한 줄에 안 들어갈 때만 **다음 줄 우측**으로 흘려보낸다 —
 * 금액은 줄이거나 자를 수 없고(₩6,988,000), 짧은 값(거래수 176건)은 한 줄에 남는 편이 낫다.
 *
 * 비교 블록의 좌우 배치처럼 열이 좁은 곳에서는 이 "될 때만 접힘"이 오히려 방해가 된다 —
 * 같은 열 안에서 어떤 행은 한 줄, 어떤 행은 두 줄이 되어 두 마켓의 행 높이가 어긋난다.
 * 그때는 `direction="column"`으로 값을 항상 아래 줄에 고정한다.
 */
const MetricRow = ({
  label,
  value,
  sub,
  onClickValue,
  underlineValue = true,
  emptyText = "-",
  onInfoClick,
  valueStyle,
  emptyValueStyle,
  direction = "row",
}: MetricRowProps) => {
  // 값이 없어 placeholder로 표시될 때는 링크 동작/밑줄 없이 별도 스타일로 렌더한다.
  const hasValue = Boolean(value);
  const appliedValueStyle: TextProps = hasValue
    ? { ...DEFAULT_VALUE_STYLE, ...valueStyle }
    : { ...DEFAULT_VALUE_STYLE, ...emptyValueStyle };

  return (
    <Flex
      flexDirection={direction}
      justifyContent={"space-between"}
      alignItems={"flex-start"}
      flexWrap={"wrap"}
      gap={"0 4px"}
    >
      <Flex gap={"2px"} alignItems={"center"} flexShrink={0}>
        <Text
          textStyle={"caption-medium"}
          color={"#8F8F8C"}
          lineHeight={"20px"}
        >
          {label}
        </Text>
        {/* 값이 있을 때만 인포 아이콘 노출 */}
        {hasValue && onInfoClick && (
          <Box
            as={"span"}
            display={"inline-flex"}
            cursor={"pointer"}
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              onInfoClick();
            }}
            color={"#8F8F8C"}
            _hover={{
              color: "#1272EF",
            }}
          >
            <CustomIcon src={"info-circle"} width={14} height={14} />
          </Box>
        )}
      </Flex>
      {/* marginLeft auto — 값이 라벨 아래로 내려가도 우측 정렬을 유지한다.
          row 에서는 줄바꿈된 줄에 space-between 이 적용되지 않아서, column 에서는
          교차축 auto 마진이 남은 가로 공간을 먹어서 — 두 경우 모두 이 한 줄로 해결된다. */}
      <Flex
        flexDirection={"column"}
        alignItems={"flex-end"}
        marginLeft={"auto"}
      >
        <Text
          {...appliedValueStyle}
          wordBreak={"break-all"}
          {...(hasValue &&
            onClickValue && {
              // 밑줄이 없으면 클릭 가능하다는 신호가 없어 hover 색으로 대신한다.
              ...(underlineValue
                ? { textDecoration: "underline" }
                : { _hover: { color: "#1272EF" } }),
              cursor: "pointer",
              onClick: (event: React.MouseEvent) => {
                event.stopPropagation();
                onClickValue();
              },
            })}
        >
          {value || emptyText}
        </Text>
        {sub && (
          <Text {...DEFAULT_VALUE_STYLE} {...valueStyle}>
            {sub}
          </Text>
        )}
      </Flex>
    </Flex>
  );
};

export default MetricRow;
